import { useSyncExternalStore } from 'react';
/**
 * The touch rules for every uxp component, in one place. They follow the input (a finger), not
 * the screen size, so they also apply to tablets held sideways.
 *
 * - Tap: the element's action, same as a click. Never a hover.
 * - Press and hold: an icon-only control shows its label; a selectable list row starts selecting.
 * - Swipe left: the item's actions, in a sheet.
 * - Hold then drag: move the item (reorder).
 * - Drag down: close a sheet.
 */
/** Press and hold this long (ms) without moving for a long press */
export const LONG_PRESS_MS = 500;
/** More movement than this (px) cancels a long press: the finger is scrolling */
export const MOVE_SLOP = 10;
/** A swipe is a mostly sideways move at least this far (px) */
export const SWIPE_DISTANCE = 60;
/** A swiped row follows the finger up to this far (px) */
export const SWIPE_FOLLOW = 80;
/** A label shown by a long press stays this long (ms) after the finger lifts */
export const TIP_SHOW_MS = 1500;
/** Mouse events this soon (ms) after a touch are the ones the browser fakes for a tap */
export const FAKE_MOUSE_MS = 1000;
/** Reordering starts after holding this long (ms)... */
export const DRAG_HOLD_MS = 250;
/** ...while the finger moves less than this (px) */
export const DRAG_SLOP = 5;
let lastTouch = 0;
if (typeof document !== 'undefined') {
    // The browser fakes its mouse events after the finger lifts, so the end counts too
    const touched = () => { lastTouch = Date.now(); };
    document.addEventListener('touchstart', touched, { capture: true, passive: true });
    document.addEventListener('touchend', touched, { capture: true, passive: true });
}
/** When the last touch began or ended (ms since epoch), 0 if never */
export const lastTouchAt = () => lastTouch;
/**
 * True for a mouse event the browser fakes after a tap (mouseenter, mouseover). A hover effect
 * that checks it is never left stuck open by a tap.
 * @export
 */
export const isFakeMouse = () => Date.now() - lastTouch < FAKE_MOUSE_MS;
const TOUCH_QUERY = '(pointer: coarse)';
const subscribeTouch = (onChange) => {
    const query = window.matchMedia(TOUCH_QUERY);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
};
/**
 * True on a touch screen (a finger is the main input), whatever the screen size. The JS twin of
 * the `on-touch` SCSS mixin: use it when touch needs different markup, not just different styles.
 * @export
 */
export const useIsTouchScreen = () => useSyncExternalStore(subscribeTouch, () => window.matchMedia(TOUCH_QUERY).matches, () => false);
